import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import {
  useGetInboundAliasQuery,
  useRotateInboundAliasMutation,
  useSetInboundAliasEnabledMutation,
} from 'src/services/inbound-email/inbound-email.service';

import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';

export default function ForwardingAddressSettingsCard() {
  const { t } = useTranslate();
  const authContext = useAuthContext();
  const rotateConfirm = useBoolean();
  const [isRotating, setIsRotating] = useState(false);

  const orgId = authContext.organization.id;

  const { currentData: alias, isLoading } = useGetInboundAliasQuery(orgId);
  const [setEnabled, { isLoading: isToggling }] = useSetInboundAliasEnabledMutation();
  const [rotate] = useRotateInboundAliasMutation();

  const canEdit = useMemo(
    () => [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role),
    [authContext]
  );

  const handleCopy = async () => {
    if (!alias?.full_address) return;
    try {
      await navigator.clipboard.writeText(alias.full_address);
      enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy', { variant: 'error' });
    }
  };

  const handleToggle = async (enabled: boolean) => {
    try {
      await setEnabled({ organizationId: orgId, enabled }).unwrap();
      enqueueSnackbar(
        enabled ? 'Inbound email enabled' : 'Inbound email disabled',
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar('Could not update inbound email setting', { variant: 'error' });
    }
  };

  const handleRotate = async () => {
    setIsRotating(true);
    try {
      await rotate(orgId).unwrap();
      enqueueSnackbar('Forwarding address rotated', { variant: 'success' });
      rotateConfirm.onFalse();
    } catch {
      enqueueSnackbar('Could not rotate forwarding address', { variant: 'error' });
    } finally {
      setIsRotating(false);
    }
  };

  const lastReceivedLabel = alias?.last_received_at
    ? new Date(alias.last_received_at).toLocaleString()
    : 'Never';

  return (
    <Card>
      <CardHeader
        avatar={<Iconify icon="mdi:email-arrow-left-outline" width={24} />}
        title="Forwarding address"
        subheader="Receive job-board applications by forwarding them to a unique JobTetris address"
      />
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack>
              <Typography variant="subtitle2">Inbound email processing</Typography>
              <Typography variant="caption" color="text.secondary">
                When enabled, applications forwarded to your address below are auto-imported.
              </Typography>
            </Stack>
            <Switch
              checked={alias?.enabled ?? false}
              disabled={!canEdit || isLoading || isToggling}
              onChange={(e) => handleToggle(e.target.checked)}
            />
          </Stack>

          {alias?.enabled && alias?.full_address ? (
            <>
              <TextField
                label="Your forwarding address"
                value={alias.full_address}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopy} edge="end" size="small" aria-label="Copy">
                        <Iconify icon="mdi:content-copy" width={20} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" spacing={4}>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    Last received
                  </Typography>
                  <Typography variant="body2">{lastReceivedLabel}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    Received in last 7 days
                  </Typography>
                  <Typography variant="body2">{alias.count_7d}</Typography>
                </Stack>
              </Stack>

              <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.neutral' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Setup guide
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    1. In Gmail or Outlook, open the inbox that receives applications from job boards (Seek, LinkedIn, Indeed, etc.).
                  </Typography>
                  <Typography variant="body2">
                    2. Create a forwarding rule to send those messages to the address above.
                  </Typography>
                  <Typography variant="body2">
                    3. Make sure each job on JobTetris has its <strong>Job Board Code</strong> visible so the recruiter can paste it into the application reference field on the board.
                  </Typography>
                </Stack>
              </Card>

              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                {canEdit && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<Iconify icon="mdi:refresh" />}
                    onClick={rotateConfirm.onTrue}
                    disabled={isRotating}
                  >
                    Rotate address
                  </Button>
                )}
              </Stack>
            </>
          ) : null}
        </Stack>
      </CardContent>

      <ConfirmDialog
        open={rotateConfirm.value}
        onClose={rotateConfirm.onFalse}
        title="Rotate forwarding address?"
        content={
          <>
            <Typography variant="body2" gutterBottom>
              This will generate a new alias token. Your current forwarding rules will stop delivering applications until you update them with the new address.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use this if the current address has leaked or you suspect misuse.
            </Typography>
          </>
        }
        action={
          <Button variant="contained" color="warning" onClick={handleRotate} disabled={isRotating}>
            Rotate
          </Button>
        }
      />
    </Card>
  );
}
